import { Clock, Shield, Zap, CheckCircle2, Check } from 'lucide-react';

interface FeatureItem {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const signUpFeatures: FeatureItem[] = [
  {
    icon: <Clock size={18} />,
    title: '7-Day Full Access',
    description: 'Complete access to all features during your trial.',
  },
  {
    icon: <Shield size={18} />,
    title: 'No Credit Card Required',
    description: 'Start immediately without payment information. Upgrade only when you\'re ready.',
  },
  {
    icon: <Zap size={18} />,
    title: 'Cancel Anytime',
    description: 'No contracts or commitments. Cancel with one click if it\'s not right for you.',
  },
  {
    icon: <CheckCircle2 size={18} />,
    title: 'Secure OAuth Sign-In',
    description: 'Connect with GitHub or GitLab for seamless repository access and enhanced security.',
  },
];

const TrustSignals = ({ compact = false }: { compact?: boolean }) => (
  <div className={compact ? 'mt-4' : 'mt-10'}>
    <div className={`flex flex-wrap items-center justify-center text-ui-text-tertiary dark:text-[#666666] ${compact ? 'gap-3' : 'gap-x-6 gap-y-3'}`}>
      <div className={`flex items-center ${compact ? 'gap-1.5' : 'gap-2'}`}>
        <Shield className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
        <span className={compact ? 'text-[10px]' : 'text-xs'}>Ephemeral Scanning: Code Never Stored</span>
      </div>
      <div className={`flex items-center ${compact ? 'gap-1.5' : 'gap-2'}`}>
        <Check className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
        <span className={compact ? 'text-[10px]' : 'text-xs'}>SOC 2 &amp; ISO 27001 Ready</span>
      </div>
      <div className={`flex items-center ${compact ? 'gap-1.5' : 'gap-2'}`}>
        <Check className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
        <span className={compact ? 'text-[10px]' : 'text-xs'}>GDPR Compliant</span>
      </div>
      <div className={`flex items-center ${compact ? 'gap-1.5' : 'gap-2'}`}>
        <Shield className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
        <span className={compact ? 'text-[10px]' : 'text-xs'}>Self-Hosted Runners Available</span>
      </div>
    </div>
    <p className={`text-center text-ui-text-tertiary dark:text-[#666666] ${compact ? 'text-[10px] mt-2' : 'text-xs mt-4'}`}>
      Start with a 7-day free trial — no card required. Paid plans from $39/month after your trial.
    </p>
  </div>
);

interface AuthMarketingPanelProps {
  variant: 'signin' | 'signup';
}

const AuthMarketingPanel = ({ variant }: AuthMarketingPanelProps) => {
  if (variant === 'signin') {
    return (
      <>
        {/* Mobile: compact banner above auth widget */}
        <div className="lg:hidden text-center mb-6">
          <h1 className="text-2xl font-bold text-ui-text-primary mb-1">
            Welcome Back to Kolega.dev
          </h1>
          <p className="text-sm text-ui-text-secondary">
            Sign in to continue monitoring and fixing your codebase vulnerabilities.
          </p>
          <TrustSignals compact />
        </div>

        {/* Desktop: left-side panel */}
        <div className="hidden lg:flex flex-col justify-center pr-12">
          <h1 className="text-5xl font-bold text-ui-text-primary leading-tight mb-4">
            Welcome Back to Kolega.dev
          </h1>
          <p className="text-lg text-ui-text-secondary">
            Sign in to continue monitoring and fixing your codebase vulnerabilities.
          </p>
          <TrustSignals />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Mobile: compact banner above auth widget */}
      <div className="lg:hidden text-center mb-6">
        <h1 className="text-2xl font-bold text-ui-text-primary mb-1">
          Start Your Free Security Scan
        </h1>
        <p className="text-sm text-ui-text-secondary mb-4">
          Join thousands of developers using AI-native security to fix technical debt automatically.
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {signUpFeatures.map((feature) => (
            <span
              key={feature.title}
              className="flex items-center gap-1.5 text-xs text-ui-text-secondary bg-neutral-200 dark:bg-ui-background-tertiary rounded-full px-3 py-1"
            >
              <span className="text-lime-600 dark:text-[#a3e635]">{feature.icon}</span>
              {feature.title}
            </span>
          ))}
        </div>
        <TrustSignals compact />
      </div>

      {/* Desktop: left-side panel */}
      <div className="hidden lg:flex flex-col justify-center pr-12">
        <h1 className="text-5xl font-bold text-ui-text-primary leading-tight mb-4">
          Start Your Free Security Scan
        </h1>
        <p className="text-lg text-ui-text-secondary mb-10">
          Join thousands of developers using AI-native security to fix technical debt automatically.
        </p>
        <ul className="space-y-6">
          {signUpFeatures.map((feature) => (
            <li key={feature.title} className="flex items-start gap-4">
              <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-lime-100 dark:bg-[#1a2a1a] text-lime-600 dark:text-[#a3e635] flex items-center justify-center">
                {feature.icon}
              </span>
              <div>
                <p className="text-ui-text-primary font-semibold text-sm">{feature.title}</p>
                <p className="text-ui-text-secondary text-sm mt-0.5">{feature.description}</p>
              </div>
            </li>
          ))}
        </ul>
        <TrustSignals />
      </div>
    </>
  );
};

export default AuthMarketingPanel;
